import { ReactNode } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { ProgressSpinner } from 'primereact/progressspinner';
import ChangeLine, { changeText } from '../../components/novarium/ChangeLine';
import { ServiceStatisticsGranularity, ServiceStatisticsMetricsDTO } from '../../dto/ServiceStatisticsDTO';
import { formatDuration, openAttentionNotice } from './periodDetail';
import { modeMessageId, StatisticsMode } from './statisticsMode';
import { percentVariation, pointsVariation, Variation, Verdict, waitVerdict } from './statisticsChange';
import { formatInt, formatMinutes, formatPercent, TimelinePoint } from './statisticsUtils';

/** Contra qué se compara lo que se mira: el promedio del período de la pantalla, o lo habitual del servicio. */
export interface KpiReference {
    kind: 'period' | 'usual';
    /** Atenciones de un período habitual del mismo largo (día, semana o mes). */
    attentions: number;
    /** Espera media habitual; null si no hay con qué comparar. */
    wait: number | null;
    /** Proporción habitual de atenciones con más de una hora de espera (fracción); null si no hay. */
    over60: number | null;
}

interface PeriodDetailKpisProps {
    /** Lo que se mira: sus atenciones y su espera se ven al instante, vienen del gráfico o de la fila del servicio. */
    point: TimelinePoint;
    granularity: ServiceStatisticsGranularity;
    /** Indicadores de lo que se mira (de la consulta del panel); null mientras carga. */
    metrics: ServiceStatisticsMetricsDTO | null;
    reference: KpiReference;
    /** En «Mi desempeño» el rótulo de las atenciones habla de las del profesional. */
    mode?: StatisticsMode;
}

/** «▲ 20 % más que el promedio del período (310)»; sin variación, nada. */
function changeLine(variation: Variation | null, reference: string, verdict: Verdict = null): ReactNode {
    return variation ? <ChangeLine variation={variation} period={reference} verdict={verdict} /> : null;
}

interface KpiProps {
    label: string;
    value: ReactNode;
    /** Renglones debajo: la cantidad detrás del porcentaje y la comparación. */
    details: ReactNode[];
}

function Kpi({ label, value, details }: KpiProps) {
    return (
        <div className="ss-detail__kpi">
            <dt className="ss-detail__kpi-label">{label}</dt>
            <dd className="ss-detail__kpi-value">{value}</dd>
            {details.filter(Boolean).length > 0 && (
                <dd className="ss-detail__kpi-detail">
                    {details.filter(Boolean).map((detail, index) => <span key={index}>{detail}</span>)}
                </dd>
            )}
        </div>
    );
}

/**
 * Lo que se dice en palabras antes de los indicadores: «Espera media 162,5 min, 350 % más que el promedio del período».
 * Sin con qué comparar, solo la espera.
 */
function summarySentence(intl: IntlShape, point: TimelinePoint, reference: KpiReference): string | null {
    if (point.avgWait === null) return null;
    const value = formatMinutes(point.avgWait);
    const change = reference.wait === null ? null : percentVariation(point.avgWait, reference.wait);
    if (!change || change.trend === 'same') return intl.formatMessage({ id: 'ServicesStatisticsPeriodDetailSummaryPlain' }, { value });
    const text = changeText(intl, change, intl.formatMessage({ id: referenceNameId(reference.kind) }));
    return intl.formatMessage({ id: 'ServicesStatisticsPeriodDetailSummary' }, { value, change: text });
}

const referenceNameId = (kind: KpiReference['kind']): string =>
    (kind === 'usual' ? 'ServicesStatisticsPeriodDetailUsualName' : 'ServicesStatisticsPeriodDetailAverageName');

/**
 * El resumen en palabras y los indicadores de lo que se mira: atenciones, espera media y «Más de 1 hora», en filas
 * compactas, cada uno contra el promedio del período de la pantalla (un día no se compara con «el período anterior», que
 * sería el día anterior) o, si se mira un servicio, contra su propio habitual. Si una sola atención esperó muchísimo más
 * que el resto, un aviso dice cuánto sería la espera sin ella: puede ser una atención que no se cerró.
 */
export default function PeriodDetailKpis({ point, granularity, metrics, reference, mode = 'management' }: PeriodDetailKpisProps) {
    const intl = useIntl();
    const referenceName = intl.formatMessage({ id: referenceNameId(reference.kind) });
    const refText = (value: string) => intl.formatMessage({ id: 'ServicesStatisticsPeriodDetailVsReference' }, { name: referenceName, value });

    const attentionsChange = reference.attentions > 0 ? percentVariation(point.attentions, reference.attentions) : null;
    const wait = point.avgWait;
    const waitChange = wait === null || reference.wait === null ? null : percentVariation(wait, reference.wait);

    const over60 = metrics && metrics.attentions > 0 ? metrics.waitOver60 / metrics.attentions : null;
    const over60Change = pointsVariation(intl, over60, reference.over60);
    const notice = metrics ? openAttentionNotice(metrics) : null;
    const summary = summarySentence(intl, point, reference);

    return (
        <section className="ss-detail__section" aria-labelledby="ss-detail-kpis-title">
            {summary && <p className="nv-sr-only" role="status">{summary}</p>}
            <h3 className="nv-section-title" id="ss-detail-kpis-title">
                {intl.formatMessage({ id: 'ServicesStatisticsPeriodDetailKpisTitle' })}
            </h3>
            <dl className="ss-detail__kpis">
                <Kpi
                    label={intl.formatMessage({ id: modeMessageId(mode, 'KpiAttentions') })}
                    value={formatInt(point.attentions)}
                    details={[changeLine(attentionsChange, refText(formatInt(reference.attentions)))]}
                />
                <Kpi
                    label={intl.formatMessage({ id: 'ServicesStatisticsKpiWait' })}
                    value={formatMinutes(wait)}
                    details={[changeLine(waitChange, refText(formatMinutes(reference.wait)),
                        waitChange ? waitVerdict(waitChange.trend) : null)]}
                />
                <Kpi
                    label={intl.formatMessage({ id: 'ServicesStatisticsKpiWait60' })}
                    value={metrics ? (over60 === null ? '—' : formatPercent(over60)) : '…'}
                    details={metrics ? [
                        intl.formatMessage({ id: 'ServicesStatisticsKpiWait60Count' },
                            { count: metrics.waitOver60, countText: formatInt(metrics.waitOver60) }),
                        changeLine(over60Change, refText(reference.over60 === null ? '—' : formatPercent(reference.over60)),
                            over60Change ? waitVerdict(over60Change.trend) : null),
                    ] : [
                        <span key="loading" className="nv-loading" role="status">
                            <ProgressSpinner strokeWidth="4" style={{ width: 16, height: 16 }} />
                            <span>{intl.formatMessage({ id: 'ServicesStatisticsLoading' })}</span>
                        </span>,
                    ]}
                />
            </dl>
            {notice && (
                <p className="nv-note ss-detail__notice" role="status">
                    <i className="pi pi-info-circle" aria-hidden="true" />
                    <span>
                        {intl.formatMessage({ id: 'ServicesStatisticsPeriodDetailOpenAttention' }, {
                            wait: formatDuration(notice.maxWaitMinutes),
                            average: formatDuration(notice.averageWithoutIt),
                            unit: granularity,
                        })}
                    </span>
                </p>
            )}
        </section>
    );
}
